import {assetUrl, fontString, loadFace, type CertificateFace} from './fonts'
import {
  CURRENT_CERTIFICATE_DESIGN,
  certificateLayout,
  type CertificateField,
  type CertificateFormat,
  type CertificateLineKey,
  type CertificateLines,
} from './layouts'
import {faceStack, paragraphDirection, prepareText} from './text'

export type RenderCertificateOptions = {
  format: CertificateFormat
  lines: CertificateLines
  templateKey?: string
  scale?: number
  assetBaseUrl?: string
}

type Row = {text: string; width: number; direction: CanvasDirection}

const images = new Map<string, Promise<HTMLImageElement>>()

function loadImage(src: string): Promise<HTMLImageElement> {
  const existing = images.get(src)
  if (existing) {
    return existing
  }

  const loading = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.decoding = 'async'
    image.onload = () => resolve(image)
    image.onerror = () => {
      images.delete(src)
      reject(new Error(`The certificate background ${src} could not be loaded.`))
    }
    image.src = src
  })
  images.set(src, loading)
  return loading
}

function measure(
  context: CanvasRenderingContext2D,
  text: string,
  stack: CertificateFace[],
  size: number
): number {
  context.font = fontString(stack, size)
  context.direction = paragraphDirection(text)
  return context.measureText(text).width
}

function fitSize(field: CertificateField, widthAtFullSize: number, maxWidth: number): number {
  const size = Math.min(field.size, (field.size * maxWidth) / widthAtFullSize)
  return size >= field.minSize ? Math.floor(size * 2) / 2 : size
}

function layOut(
  context: CanvasRenderingContext2D,
  text: string,
  stack: CertificateFace[],
  field: CertificateField
): {rows: Row[]; size: number} {
  const maxWidth = field.maxWidth ?? field.width
  const fullWidth = measure(context, text, stack, field.size)
  const spaces = Array.from(text, (character, index) => (character === ' ' ? index : -1)).filter(
    (index) => index >= 0
  )
  const row = (rowText: string, size: number): Row => ({
    text: rowText,
    width: measure(context, rowText, stack, size),
    direction: paragraphDirection(rowText),
  })

  if (
    (field.maxLines ?? 1) === 1 ||
    spaces.length === 0 ||
    (fullWidth * field.minSize) / field.size <= maxWidth
  ) {
    const size = fitSize(field, fullWidth, maxWidth)
    return {size, rows: [row(text, size)]}
  }

  const middle = Math.floor(text.length / 2)
  const cut = spaces.reduce((best, index) =>
    Math.abs(index - middle) < Math.abs(best - middle) ? index : best
  )
  const halves = [text.slice(0, cut), text.slice(cut + 1)]
  const widest = Math.max(...halves.map((half) => measure(context, half, stack, field.size)))
  const size = fitSize(field, widest, maxWidth)
  return {size, rows: halves.map((half) => row(half, size))}
}

function baseline(stack: CertificateFace[], size: number, top: number, lineHeight: number): number {
  const ascent = stack[0].metrics.ascent * size
  const descent = stack[0].metrics.descent * size
  return top + (lineHeight - (ascent + descent)) / 2 + ascent
}

export async function renderCertificate(
  options: RenderCertificateOptions
): Promise<HTMLCanvasElement> {
  const layout = certificateLayout(options.templateKey || CURRENT_CERTIFICATE_DESIGN)
  const format = layout.formats[options.format]
  const scale = options.scale ?? format.defaultScale
  const assetBaseUrl = options.assetBaseUrl ?? ''

  const fields = format.fields.map((field) => {
    const raw = options.lines[field.line]
    if (!raw || !raw.trim()) {
      return {field, text: '', stack: [] as CertificateFace[]}
    }
    const text = prepareText(raw)
    return {field, text, stack: faceStack(field.font, text)}
  })

  const faces = new Set<CertificateFace>()
  for (const {text, stack} of fields) {
    for (const item of stack) {
      if (!item.scriptTest || item.scriptTest.test(text)) {
        faces.add(item)
      }
    }
  }

  const [background] = await Promise.all([
    loadImage(assetUrl(assetBaseUrl, format.background)),
    ...Array.from(faces, (item) => loadFace(item, assetBaseUrl)),
  ])

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(format.width * scale)
  canvas.height = Math.round(format.height * scale)
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error("This browser can't draw the certificate.")
  }

  context.scale(scale, scale)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(background, 0, 0, format.width, format.height)
  context.textBaseline = 'alphabetic'
  context.textAlign = 'left'

  const extraHeight: Partial<Record<CertificateLineKey, number>> = {}
  for (const {field, text, stack} of fields) {
    extraHeight[field.line] = 0
    if (!text) {
      continue
    }

    if (field.clearBackground) {
      context.fillStyle = layout.paperColor
      context.fillRect(field.x, field.y - 1, field.width, field.height + 2)
    }

    const {rows, size} = layOut(context, text, stack, field)
    const lineHeight = (field.height * size) / field.size
    if (rows.length > 1) {
      extraHeight[field.line] = lineHeight
    }

    const top = field.y + (field.flowAfterLine ? extraHeight[field.flowAfterLine] ?? 0 : 0)
    context.font = fontString(stack, size)
    context.fillStyle = field.color
    rows.forEach((row, index) => {
      const y = baseline(
        stack,
        size,
        top + index * lineHeight,
        rows.length === 1 ? field.height : lineHeight
      )
      const x =
        field.align === 'left'
          ? field.x
          : field.align === 'right'
          ? field.x + field.width - row.width
          : field.x + (field.width - row.width) / 2
      context.direction = row.direction
      context.fillText(row.text, x, y)
    })
  }

  return canvas
}
